import React from 'react';
import { Grid, GridColumn, GridItemChangeEvent } from '@progress/kendo-react-grid';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { EditDescriptor } from '@progress/kendo-react-data-tools';
import { createCustomFieldCellWrapper, createRemoveCellWrapper, NameCell } from './FormFileMetadataCells';
import { useCurrentUserContext, useGlobalizationContext, useGetAllFields } from '@cs-cms/state-management';
import { ContentTypeFieldResponse, CPS_CONTENT_TYPE_FIELD_TYPE } from '@cs-cms/types';
import { ExtendedUploadFileInfo } from '../../../types/ExtendedUploadFileInfo';

interface FileWithMetadata {
  file: ExtendedUploadFileInfo;
  perFileMetadata: Record<string, any>;
}

enum GridFileFields {
  NAME = 'file.name',
  REMOVE = 'file.remove',
  METADATA_PREFIX = 'perFileMetadata.'
}

interface FormFileMetadataTableProps {
  show: boolean;
  files: FileWithMetadata[];
  perFileFields: ContentTypeFieldResponse[];
  getFieldChoices: (field: any) => any[];
  onChange: (files: FileWithMetadata[]) => void;
}

export const FormFileMetadataTable: React.FC<FormFileMetadataTableProps> = ({
  show,
  files,
  perFileFields,
  getFieldChoices,
  onChange
}) => {
  const { selectedIndex } = useCurrentUserContext();
  const { selectedLocale } = useGlobalizationContext();
  const { data: allFields } = useGetAllFields(selectedLocale, selectedIndex?.id);

  const edit: EditDescriptor = {};
  files.forEach(fileItem => {
    edit[fileItem.file.name] = true;
  });

  const handlersRef = React.useRef({
    getFieldChoices,
    perFileFields,
    handleRemove: (fileIndex: number) => {
      const updatedFiles = files.filter((_, index) => index !== fileIndex);
      onChange(updatedFiles);
    }
  });

  handlersRef.current = {
    getFieldChoices,
    perFileFields,
    handleRemove: (fileIndex: number) => {
      const updatedFiles = files.filter((_, index) => index !== fileIndex);
      onChange(updatedFiles);
    }
  };

  const cellWrappersRef = React.useRef<{
    RemoveCellWrapper: ReturnType<typeof createRemoveCellWrapper>;
    CustomFieldCellWrapper: ReturnType<typeof createCustomFieldCellWrapper>;
  } | null>(null);

  if (cellWrappersRef.current === null) {
    cellWrappersRef.current = {
      RemoveCellWrapper: createRemoveCellWrapper(handlersRef),
      CustomFieldCellWrapper: createCustomFieldCellWrapper(handlersRef)
    };
  }

  const { RemoveCellWrapper, CustomFieldCellWrapper } = cellWrappersRef.current;

  if (files.length === 0 || !show) {
    return null;
  }

  const handleItemChange = (event: GridItemChangeEvent) => {
    const { field, value, dataIndex } = event;

    if (!field || !field.startsWith(GridFileFields.METADATA_PREFIX)) {
      return;
    }

    const fieldName = field.replace(GridFileFields.METADATA_PREFIX, '');
    const updatedFiles = [...files];
    updatedFiles[dataIndex].perFileMetadata[fieldName] = value;
    onChange(updatedFiles);
  };

  const getEditorType = (field: ContentTypeFieldResponse) => {
    switch (field.type) {
      case CPS_CONTENT_TYPE_FIELD_TYPE.STRING:
        return 'text';
      case CPS_CONTENT_TYPE_FIELD_TYPE.INT:
      case CPS_CONTENT_TYPE_FIELD_TYPE.INT64:
      case CPS_CONTENT_TYPE_FIELD_TYPE.DECIMAL:
        return 'numeric';
      default:
        return undefined;
    }
  };

  return (
    <div>
      <Tooltip parentTitle>
        <Grid
          data={files}
          className="formMetadataGrid"
          resizable={true}
          onItemChange={handleItemChange}
          dataItemKey="file.name"
          edit={edit}
          editable={{ mode: 'inline' }}
        >
          <GridColumn
            field={GridFileFields.NAME}
            title="File Name"
            width={150}
            editable={false}
            cells={{ data: NameCell }}
          />
          {perFileFields.map(field => {
            const fieldTranslation = allFields?.find(x => x.name === field.name)?.displayName || field.name;
            const editorType = getEditorType(field);

            return (
              <GridColumn
                key={field.name}
                field={`${GridFileFields.METADATA_PREFIX}${field.name}`}
                title={fieldTranslation}
                minResizableWidth={100}
                editor={editorType}
                cells={editorType ? undefined : { data: CustomFieldCellWrapper }}
              />
            );
          })}
          <GridColumn
            field={GridFileFields.REMOVE}
            title=""
            width={60}
            editable={false}
            cells={{ data: RemoveCellWrapper }}
          />
        </Grid>
      </Tooltip>
    </div>
  );
};

FormFileMetadataTable.displayName = 'FormFileMetadataTable';
